import { computed } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import type { AbstractControl } from '@angular/forms'

export interface Requirement {
  label: string
  isValid: () => boolean
}

/**
 * Wraps plain predicates in computed signals that recompute on every form
 * event. Templates reading `isValid()` then register as reactive consumers, so
 * the requirement list is re-rendered while the user types instead of only when
 * something else happens to mark the view dirty.
 *
 * Must be called from an injection context (e.g. a component field
 * initializer).
 */
export function reactiveRequirements(
  form: AbstractControl,
  requirements: Requirement[]
): Requirement[] {
  const formEvents = toSignal(form.events)

  return requirements.map(({ label, isValid }) => ({
    label,
    isValid: computed(() => {
      formEvents()

      return isValid()
    }),
  }))
}

export function equalValidator(
  controlName: string,
  matchingControlName: string
) {
  return (group: AbstractControl) => {
    const control = group.get(controlName)
    const matchingControl = group.get(matchingControlName)
    if (!control || !matchingControl) {
      return null
    }

    let controlErrors = null,
      matchingControlErrors = null

    // set error on matchingControl if validation fails
    if (control.value !== matchingControl.value) {
      controlErrors = { ...control.errors, equal: true }
      matchingControlErrors = { ...matchingControl.errors, equal: true }
    } else {
      // If other errors, remove the equal error
      if (
        Object.keys(control.errors ?? {}).filter(e => e !== 'equal').length > 0
      ) {
        controlErrors = { ...control.errors, equal: undefined }
        matchingControlErrors = { ...matchingControl.errors, equal: undefined }
      }
    }

    control.setErrors(controlErrors)
    matchingControl.setErrors(matchingControlErrors)
    return null
  }
}

export function hasError(control: AbstractControl, error: string): boolean {
  return !!control.errors?.[error]
}

export const passwordPattern =
  /^(?=.*[A-Z])(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).*$/
